import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '../environments/environment';

export interface UserManagerAuditLogEntry {
  id: string;
  instanceId: string;
  auditDateTime: string;
  action: string;
  source: string;
  performedByUserId: string;
  performedByUserName: string;
  performedByUserEmail: string;
  entityType: string;
  entityId: string;
  entityName: string;
  details: string;
  oldValue?: string;
  newValue?: string;
}

export interface PaginatedAuditResult {
  items: UserManagerAuditLogEntry[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserManagerAuditService {
  private readonly apiUrl = `${environment.apiEndpoint}/audit/usermanager`;
  private readonly entriesSubject = new BehaviorSubject<UserManagerAuditLogEntry[]>([]);
  private readonly paginationSubject = new BehaviorSubject<PaginatedAuditResult | null>(null);

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {
    // Don't auto-reload here - let the component control when to fetch
    // this.reload();
    // this.authService.instanceChanged$.subscribe(() => this.reload());
  }

  get entries$(): Observable<UserManagerAuditLogEntry[]> {
    return this.entriesSubject.asObservable();
  }

  get pagination$(): Observable<PaginatedAuditResult | null> {
    return this.paginationSubject.asObservable();
  }

  getEntries(): UserManagerAuditLogEntry[] {
    return this.entriesSubject.value;
  }

  getPagination(): PaginatedAuditResult | null {
    return this.paginationSubject.value;
  }

  reload(): void {
    this.fetchAuditLogs().subscribe();
  }

  fetchAuditLogs(
    instanceId?: string,
    userId?: string,
    action?: string,
    source?: string,
    startDate?: Date,
    endDate?: Date,
    pageNumber: number = 1,
    pageSize: number = 50
  ): Observable<PaginatedAuditResult> {
    let params = new HttpParams();
    
    if (instanceId) {
      params = params.set('instanceId', instanceId);
    }
    if (userId) {
      params = params.set('userId', userId);
    }
    if (action) {
      params = params.set('action', action);
    }
    if (source) {
      params = params.set('source', source);
    }
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }
    params = params.set('pageNumber', pageNumber.toString());
    params = params.set('pageSize', pageSize.toString());

    return this.http.get<PaginatedAuditResult>(this.apiUrl, { params }).pipe(
      tap(result => {
        this.entriesSubject.next(result.items);
        this.paginationSubject.next(result);
      })
    );
  }

  clearAll(): void {
    // Backend audit logs cannot be cleared from frontend
    // This method is kept for compatibility but does nothing
    console.warn('Audit logs are stored in the backend and cannot be cleared from the frontend');
  }
}
